// Phase 4.5B — Consultation request modal.
// Lead-capture only. POSTs to live backend endpoint
//   POST /api/consultations/request   (4.5A; rate-limited 5/IP/hour;
//                                       requires paid session — returns
//                                       403 paid_access_required otherwise).
// Mirrors ImplementationModal shell, focus mgmt, role/aria-modal, and
// click-outside-close behavior. No Stripe. No scheduling. No calendar.
// No contract.
import { useState } from "react";
import { submitConsultationRequest } from "../../lib/api/index";
import { logger } from "../../lib/logger";

export default function ConsultationRequestModal({
  open,
  onClose,
  sessionId,
  defaultName,
  defaultEmail,
  defaultBusiness,
}) {
  const [name, setName] = useState(defaultName || "");
  const [email, setEmail] = useState(defaultEmail || "");
  const [phone, setPhone] = useState("");
  const [business, setBusiness] = useState(defaultBusiness || "");
  const [goals, setGoals] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!name.trim() || !email.trim() || !business.trim()) {
      setError("Please fill out name, email, and business.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await submitConsultationRequest({
        session_id: sessionId,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        business: business.trim(),
        goals: goals.trim(),
        preferred_time: preferredTime.trim(),
      });
      // Backend fires `consultation_request_submit`; no client-side
      // duplicate. (Telemetry registration is deferred per 4.5B ruling.)
      if (data?.ok) setSubmitted(true);
    } catch (err) {
      logger.error("consultation_request_failed", {
        sessionId, err: err?.response?.data || err?.message,
      });
      const status = err?.response?.status;
      if (status === 429) {
        setError("Too many requests. Please try again in a few minutes.");
      } else if (status === 403) {
        setError("This audit hasn't been unlocked. Please complete checkout first.");
      } else if (status === 404) {
        setError("Session not found. Please re-open your report.");
      } else if (status === 422) {
        setError("Please check your details and try again.");
      } else {
        setError("Could not submit your request. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="consult-modal"
      style={{
        position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.78)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sf-card" style={{ width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        {submitted ? (
          <div data-testid="consult-modal-thanks">
            <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Request received</div>
            <h2 className="sf-display" style={{ fontSize: 24, marginBottom: 10, lineHeight: 1.2 }}>
              Thank you.
            </h2>
            <p className="sf-muted-text" style={{ marginBottom: 22 }}>
              Request received — we&#39;ll be in touch within 48 hours.
            </p>
            <button type="button" className="sf-btn" onClick={onClose} data-testid="consult-modal-close-btn">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="consult-modal-form">
            <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Strategic consultation</div>
            <h2 className="sf-display" style={{ fontSize: 24, marginBottom: 10, lineHeight: 1.2 }} data-testid="consult-modal-title">
              Request a Strategic Consultation
            </h2>
            <p className="sf-muted-text" style={{ marginBottom: 22 }} data-testid="consult-modal-subtitle">
              60 minutes. Your audit data. A concrete action plan.
            </p>

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="sf-input" style={{ marginTop: 6, marginBottom: 16 }} required data-testid="consult-name-input" />

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="sf-input" style={{ marginTop: 6, marginBottom: 16 }} required data-testid="consult-email-input" />

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Phone</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="sf-input" style={{ marginTop: 6, marginBottom: 16 }} data-testid="consult-phone-input" />

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Business name</label>
            <input type="text" value={business} onChange={(e) => setBusiness(e.target.value)} className="sf-input" style={{ marginTop: 6, marginBottom: 16 }} required data-testid="consult-business-input" />

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>What are your top priorities?</label>
            <textarea
              className="sf-textarea"
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              style={{ marginTop: 6, marginBottom: 16 }}
              data-testid="consult-goals-input"
            />

            <label className="sf-mono" style={{ fontSize: 12, color: "var(--sf-muted)", letterSpacing: "0.12em", textTransform: "uppercase" }}>Preferred scheduling window (optional)</label>
            <input type="text" value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} className="sf-input" style={{ marginTop: 6 }} data-testid="consult-preferred-time-input" />

            {error && (
              <div className="sf-mono" style={{ color: "#e2867e", fontSize: 13, marginTop: 14 }} data-testid="consult-modal-error">
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 12, marginTop: 22 }}>
              <button type="button" className="sf-btn-secondary" onClick={onClose} data-testid="consult-modal-cancel">
                Cancel
              </button>
              <button type="submit" className="sf-btn" disabled={submitting} data-testid="consult-modal-submit">
                {submitting ? "Submitting…" : "Submit consultation request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
